import Axios from '@/utils/axios';

// ─── Developer API keys ───────────────────────────────────────────────
// Self-service key management on the logistic integration surface:
//   POST   /v1/integration/api-keys            create (plaintext returned ONCE)
//   GET    /v1/integration/api-keys            list own keys (prefix only)
//   GET    /v1/integration/api-keys/:id/events append-only history
//   POST   /v1/integration/api-keys/:id/revoke revoke (irreversible)
// JWT-authed (a key cannot mint keys); the caller's client scope comes from
// the bearer token. Same LINK channel headers as the other client endpoints.
// Backend design: nest-logistic-service docs/api-integration/api-key.md.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

/** LIVE = production traffic, TEST = sandbox. The environment is IN the
 *  credential (`dash_live_…` / `dash_test_…`): a sandbox key can never
 *  authenticate production requests and vice versa. */
export type ApiKeyEnvironment = 'LIVE' | 'TEST';

export const API_KEY_SCOPES = [
  'all',
  'shipments:read',
  'shipments:write',
  'shipments:cancel',
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export type ApiKeyEventType =
  | 'CREATED'
  | 'REVOKED'
  | 'EXPIRED'
  | 'AUTH_REJECTED';

export interface ApiKeyRow {
  id: string;
  name: string;
  environment: ApiKeyEnvironment;
  keyPrefix: string;
  scopes: ApiKeyScope[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface ApiKeyEvent {
  id: string;
  event: ApiKeyEventType;
  actor: string | null;
  note: string | null;
  createdAt: string;
}

export interface CreateApiKeyRequest {
  name: string;
  environment: ApiKeyEnvironment;
  scopes: ApiKeyScope[];
}

export interface CreateApiKeyResponse {
  id: string;
  name: string;
  environment: ApiKeyEnvironment;
  keyPrefix: string;
  scopes: ApiKeyScope[];
  expiresAt: string | null;
  createdAt: string;
  /** The full plaintext key — shown once, never retrievable again. */
  key: string;
}

interface Envelope<T> {
  status: string;
  message?: string;
  data: T;
}

const BASE = '/v1/integration/api-keys';

export const listApiKeys = async (): Promise<ApiKeyRow[]> => {
  const res = await Axios.get<Envelope<{ data: ApiKeyRow[] }>>(BASE, {
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data.data;
};

export const createApiKey = async (
  body: CreateApiKeyRequest
): Promise<CreateApiKeyResponse> => {
  const res = await Axios.post<Envelope<CreateApiKeyResponse>>(BASE, body, {
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data;
};

export const listApiKeyEvents = async (id: string): Promise<ApiKeyEvent[]> => {
  const res = await Axios.get<
    Envelope<{ keyPrefix: string; data: ApiKeyEvent[] }>
  >(`${BASE}/${id}/events`, { headers: CHANNEL_HEADERS_LIVE });
  return res.data.data.data;
};

export const revokeApiKey = async (id: string): Promise<void> => {
  await Axios.post(`${BASE}/${id}/revoke`, undefined, {
    headers: CHANNEL_HEADERS_LIVE,
  });
};
